import React from "react"
import { motion } from "framer-motion"
import Carousel from "./Carousel"
import {
  useIntersectionObserver,
  useStaggeredAnimation
} from "../hooks/useScrollAnimation"

const GALLERY_IMAGE_COUNT = 16

const galleryImageSrc = (imageNum: number) =>
  `/pics/gallery/${String(imageNum).padStart(2, "0")}.jpeg`

const Gallery: React.FC = () => {
  const { ref: galleryRef, hasIntersected } = useIntersectionObserver()
  const { ref: gridRef } = useStaggeredAnimation(150)

  const carouselImages = Array.from({ length: 1 }, (_, i) => {
    const imageNum = i + 1

    return {
      id: imageNum,
      src: galleryImageSrc(imageNum),
      alt: `Ale y Loren - Momento ${imageNum}`,
      aspectRatio: "aspect-[4/3]"
    }
  })

  const gridImages = Array.from({ length: GALLERY_IMAGE_COUNT - 1 }, (_, i) => {
    const imageNum = i + 2

    return {
      id: imageNum,
      src: galleryImageSrc(imageNum),
      alt: `Ale y Loren - Recuerdo ${imageNum}`,
      aspectRatio:
        i % 3 === 0
          ? "aspect-square"
          : i % 3 === 1
            ? "aspect-[4/3]"
            : "aspect-[3/4]"
    }
  })

  return (
    <section id="gallery" className="py-10 gradient-wedding">
      <div className="container-max section-padding">
        <motion.div
          ref={galleryRef as any}
          initial={{ opacity: 0, y: 50 }}
          animate={hasIntersected ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <motion.h2
            className="font-wasted text-4xl font-normal text-wedding-primary mb-6"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            Nuestra galería
          </motion.h2>
          <motion.p
            className="font-avenir text-lg md:text-xl text-sage-700 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={hasIntersected ? { opacity: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Diez años, muchos recuerdos y unas cuantas "metas" cruzadas 💕.
          </motion.p>
        </motion.div>

        {/* Main Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={hasIntersected ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.4 }}
          className="mb-16"
        >
          <div className="card-wedding p-8">
            <Carousel
              items={carouselImages}
              autoPlay={true}
              autoPlayInterval={5000}
            />
          </div>
        </motion.div>

        {/* Additional Photos Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={hasIntersected ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mb-16"
        >
          <h3 className="font-script text-2xl md:text-3xl font-semibold text-wedding-secondary text-center mb-8">
            Más recuerdos
          </h3>

          <div
            ref={gridRef as any}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {gridImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 1, y: 0, scale: 1 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6 }}
                whileHover={{
                  scale: 1.03,
                  y: -5,
                  transition: { duration: 0.3 }
                }}
                className="group cursor-pointer"
              >
                <div className="card-wedding overflow-hidden">
                  <div
                    className={`${image.aspectRatio} relative overflow-hidden`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Decorative corner */}
                    <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={hasIntersected ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="text-center"
        >
          <div className="card-wedding p-8 md:p-12 max-w-4xl mx-auto">
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
                rotate: [0, 2, -2, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="text-6xl mb-6"
            >
              📸
            </motion.div>

            <h3 className="font-script text-3xl md:text-4xl font-bold text-wedding-primary mb-4">
              ¡Ayúdanos a crear más recuerdos!
            </h3>

            <p className="font-avenir text-sage-700 mb-6 text-lg leading-relaxed">
              El día de la boda, haz fotos, graba vídeos y no nos escondas nada 🙈. <br /> ¡Nos encantará revivir cada momento una y otra vez!
            </p>

            <p className="font-avenir text-sage-700 mb-6 text-lg leading-relaxed">
              🔜 Más adelante compartiremos la plataforma donde podréis subir todo.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Gallery
